import { ConfigurationContext } from '@portals/fifi/src/contexts/configuration';
import { useYellowMapsAutoComplete } from '@portals/maps/src/hooks/useYellowMapsAutoComplete';
import axios from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { SiteSearchCategory } from '../../models';
import { SearchCategory } from '../../pages/api/search-v2/constants';
import { OnSiteSearchApiResource as Search, SearchCategoryType } from '../../pages/api/search-v2/types';
import { useDebounce } from '../hooks/useDebounce';
import { useOptionalRouter } from '../hooks/useOptionalRouter';

export type { OnSiteSearchApiResource as Search } from '../../pages/api/search-v2/types';

export type OnSiteSearchV2ContextValue = {
  search: Search | null;
  siteSearchCategories: SiteSearchCategory[] | null;
  inputValue: string;
  isLoading: boolean;
  onInputChange: (value: string) => void;
  onInputChangeByClick: (value: string) => void;
  loadData: (offset: number, limit: number, siteSearchCategory?: SearchCategoryType) => Promise<Search>;
};

const API_ENTRY_POINT = '/api/search-v2';
const SEARCH_QUERY_PARAM = 'query';

const OnSiteSearchV2Context = createContext<OnSiteSearchV2ContextValue | null>(null);

export { OnSiteSearchV2Context, SEARCH_QUERY_PARAM };

/**
 * Search categories in the desired order.
 */
const SITE_SEARCH_CATEGORY_ORDER: SearchCategoryType[] = ['produkte', 'ratgeber', 'fifi', 'jobs', 'pdf'];

const INITIAL_OFFSET = 0;
const INITIAL_LIMIT = 6;

interface Props {
  children: React.ReactNode;
  initialSiteSearchCategories: SiteSearchCategory[];
}

/**
 * Helper function for consistent error handling.
 */
function handleRequestError(error: unknown) {
  console.error('[OnSiteSearch] Request Error:', error);
}

/**
 * Loads site search categories from the server.
 */
async function fetchSiteSearchCategories(signal: AbortSignal): Promise<SiteSearchCategory[]> {
  const response = await axios.get<SiteSearchCategory[]>('/api/site-search-categories', {
    signal,
  });
  return response.data;
}

/**
 * Loads search results for a given `query` from the server.
 */
async function fetchSearchResults(query: string, signal: AbortSignal): Promise<Search> {
  const response = await axios.get<Search>(API_ENTRY_POINT, {
    signal,
    params: { query, offset: INITIAL_OFFSET, limit: INITIAL_LIMIT },
  });
  return response.data;
}

/**
 * Generic loader
 */
async function loadMoreData(
  query: string,
  offset: number,
  limit: number,
  siteSearchCategory?: SearchCategoryType,
  signal?: AbortSignal
): Promise<Search> {
  const response = await axios.get<Search>(API_ENTRY_POINT, {
    signal,
    params: {
      query,
      offset,
      limit,
      siteSearchCategory,
    },
  });
  return response.data;
}

/**
 * Context provider component for the on-site search.
 */
export const OnSiteSearchV2ContextProvider: React.FC<Props> = ({ children, initialSiteSearchCategories = [] }) => {
  const router = useOptionalRouter();

  // If there is a query in the URL, use it as the initial state
  const queryParam = router.query[SEARCH_QUERY_PARAM]?.toString() ?? '';

  const [inputValue, setInputValue] = useState<string>(queryParam);
  const [search, setSearch] = useState<Search | null>(null);
  const [siteSearchCategories, setSiteSearchCategories] = useState<SiteSearchCategory[] | null>(
    initialSiteSearchCategories
  );
  const [isLoading, setIsLoading] = useState(false);

  // Debounce
  const debouncedInputValue = useDebounce(inputValue, 300);

  // Refs for abort control (AbortController)
  const categoriesAbortRef = useRef<AbortController | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);

  // Context from fifi + Hook for YellowMaps
  const configContext = useContext(ConfigurationContext);
  const { features } = useYellowMapsAutoComplete(configContext, {
    [SEARCH_QUERY_PARAM]: inputValue,
    filterOptions: {
      excludedGeoEntities: ['STATE', 'COUNTRY', 'ATTRACTION'],
    },
  });

  /**
   * Syncs URL parameters with the debounced input value.
   */
  useEffect(() => {
    if (!router) {
      return;
    }

    const urlSearchParams = new URLSearchParams(window.location.search);
    if (debouncedInputValue) {
      urlSearchParams.set(SEARCH_QUERY_PARAM, debouncedInputValue);
    } else {
      urlSearchParams.delete(SEARCH_QUERY_PARAM);
    }

    // Shallow routing to avoid a full page refresh
    router.push(
      {
        pathname: window.location.pathname,
        search: urlSearchParams.toString(),
      },
      undefined,
      { shallow: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue]);

  /**
   * Loads the site search categories if initialSiteSearchCategories is empty.
   */
  useEffect(() => {
    // If we already have categories, do nothing
    if (initialSiteSearchCategories.length > 0) {
      return;
    }

    // If the previous request is still open, abort it
    if (categoriesAbortRef.current) {
      categoriesAbortRef.current.abort();
    }
    categoriesAbortRef.current = new AbortController();

    fetchSiteSearchCategories(categoriesAbortRef.current.signal)
      .then((data) => {
        setSiteSearchCategories(data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        handleRequestError(error);
      });

    // Cleanup: Abort controller when unmounting or re-starting
    return () => categoriesAbortRef.current?.abort();
  }, [initialSiteSearchCategories]);

  /**
   * Loads search results if the query parameter is set.
   */
  useEffect(() => {
    // If there is no query in the URL, set search result to null
    if (!queryParam) {
      setSearch(null);
      return;
    }

    // Abort the previous request
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    searchAbortRef.current = new AbortController();

    setIsLoading(true);

    fetchSearchResults(queryParam, searchAbortRef.current.signal)
      .then((data) => {
        setSearch(data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return;
        }
        handleRequestError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // Cleanup
    return () => searchAbortRef.current?.abort();
  }, [queryParam]);

  const onInputChange = useCallback((newValue: string) => {
    setInputValue(newValue);
  }, []);

  const onInputChangeByClick = useCallback((newValue: string) => {
    window.scrollTo({ top: 0 });
    setInputValue(newValue);
  }, []);

  /**
   * External function for pagination, etc.
   * Loads additional data if queryParam is present.
   */
  const loadData = useCallback(
    async (offset: number, limit: number, siteSearchCategory?: SearchCategoryType): Promise<Search> => {
      const localAbortController = new AbortController();

      try {
        const data = await loadMoreData(queryParam, offset, limit, siteSearchCategory, localAbortController.signal);
        return data;
      } catch (error) {
        if (!axios.isCancel(error)) {
          handleRequestError(error);
        }
        throw error;
      }
    },
    [queryParam]
  );

  /**
   * Memoized search result:
   * - Integrates the features from useYellowMapsAutoComplete (fifi).
   * - Returns null if `search` doesn't exist.
   */
  const searchMemo = useMemo<Search | null>(() => {
    if (!search) {
      return null;
    }

    console.log('Search:', search.entries[SearchCategory.Faq].items[0].question);

    // Add the "fifi" entries from features (totalCount = -1 as a placeholder)
    return {
      ...search,
      entries: {
        ...search.entries,
        fifi: {
          items: features ? [...features] : [],
          totalCount: -1,
        },
      },
    };
  }, [search, features]);

  /**
   * Sorted categories in the desired order.
   */
  const orderedSiteSearchCategories = useMemo(() => {
    if (!siteSearchCategories) {
      return null;
    }
    return [...siteSearchCategories].sort(
      (a, b) =>
        SITE_SEARCH_CATEGORY_ORDER.indexOf(a.key as SearchCategoryType) -
        SITE_SEARCH_CATEGORY_ORDER.indexOf(b.key as SearchCategoryType)
    );
  }, [siteSearchCategories]);

  /**
   * Assemble the context value.
   */
  const contextValue: OnSiteSearchV2ContextValue = useMemo(
    () => ({
      search: searchMemo,
      siteSearchCategories: orderedSiteSearchCategories,
      inputValue,
      isLoading,
      onInputChange,
      onInputChangeByClick,
      loadData,
    }),
    [searchMemo, orderedSiteSearchCategories, inputValue, isLoading, onInputChange, onInputChangeByClick, loadData]
  );

  return <OnSiteSearchV2Context.Provider value={contextValue}>{children}</OnSiteSearchV2Context.Provider>;
};

export const useOnSiteSearchV2Context = (): OnSiteSearchV2ContextValue => {
  const context = useContext(OnSiteSearchV2Context);
  if (!context) {
    throw new Error('useOnSiteSearchV2Context must be used within a OnSiteSearchV2ContextProvider');
  }
  return context;
};
